import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CompleteCarton,
  DeleteCarton,
  FillLineMap,
  InitWipPackerStore,
  LoadBctxData,
  LoadBctxDataSuccess,
  NoWipPackerAction,
  SaveCarton,
  SaveCartonFailure,
  SaveCartonSuccess,
  SetCurrentCarton,
  SetSelectedBctxAndHeader,
  UndoCarton,
  UpdateWipPackerStore,
} from './wip-packer.actions';
import { TWipPackerStoreActionsType } from './wip-packer.actions';
import { catchError } from 'rxjs/operators';
import { map, take, switchMap } from 'rxjs/operators';
import { InputAppAPIHelperUtil } from '@monorepo/utils';
import { WipPackerStoreFacade } from './wip-packer.facade';
import { TranslateService } from '@ngx-translate/core';
import { AppNotificationUtilClass, InputAppDataService } from '@monorepo/services';
import { WipPackerDataService } from '@monorepo/services';
import { from, of } from 'rxjs';
import { MasterdataListDBFacade } from '../master-data-list-db/master-data-list-db.facade';
import { InitStoreFacade } from '../init-db/init-db.facade';
import { getWipPackerStoreInitalState } from './wip-packer.reducer';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import produce from 'immer';
import { TWipPackerCarton, TWipPackerCounts, TWipPackerLandingViewDescriptor } from '@monorepo/types';
import { AppMasterDBFacade } from '@monorepo/core-data';

@Injectable({
  providedIn: 'root',
})
export class WipPackerStoreEffects {
  constructor(
    private action$: Actions,
    private inputAppAPIHelperUtil: InputAppAPIHelperUtil,
    private wipPackerStoreFacade: WipPackerStoreFacade,
    private translateService: TranslateService,
    private inputAppDataService: InputAppDataService,
    private masterdataListDBFacade: MasterdataListDBFacade,
    private wipPackerDataService: WipPackerDataService,
    private appNotificationService: AppNotificationUtilClass,
    private initStoreFacade: InitStoreFacade,
    private persistentStorageService: PersistentStorageService,
    private appMasterDBFacade: AppMasterDBFacade,
  ) {}

  InitWipPackerStore$ = createEffect(() => {
    return this.action$.pipe(
      ofType<InitWipPackerStore>(TWipPackerStoreActionsType.InitWipPackerStore),
      switchMap((action) => {
        return this.initStoreFacade.getNextCacheResetTimestamp().pipe(
          take(1),
          map((nextCacheRestTimestamp) => [nextCacheRestTimestamp] as const),
        );
      }),
      switchMap(([nextCacheRestTimestamp]) => {
        return this.wipPackerStoreFacade.getPageConfigByViewName('pkg_landing_page').pipe(
          take(1),
          map((pageConfig) => [nextCacheRestTimestamp, pageConfig as TWipPackerLandingViewDescriptor] as const),
        );
      }),
      switchMap(([nextCacheRestTimestamp, pageConfig]) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            if (res && res.nextCacheResetTimestamp === nextCacheRestTimestamp) return of(new UpdateWipPackerStore(res));
            else {
              const wipPackerInitialState = getWipPackerStoreInitalState(nextCacheRestTimestamp);
              let updatedWipPackerState = wipPackerInitialState;
              if (res) {
                updatedWipPackerState = produce(updatedWipPackerState, (draft) => {
                  const cacheTime = (pageConfig.max_cache_days || 3) * 24 * 60 * 60 * 1000;
                  const currTime = new Date().getTime();

                  Object.entries(res.counts).forEach(([groupKey, count]) => {
                    if (count.lastUpdatedTimestamp + cacheTime > currTime) {
                      draft.counts[groupKey] = count;
                    }
                  });
                  const currentCartons = res.currentCartons.filter(
                    (item) => item.status === 'OPEN' && item.datetime + cacheTime > currTime,
                  );
                  draft.currentCartons = currentCartons;

                  draft.selectedBctx = currentCartons.length > 0 ? res.selectedBctx : [];
                  draft.selectedBctxHeader = currentCartons.length > 0 ? res.selectedBctxHeader : [];
                });
              }
              return from(
                this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedWipPackerState),
              ).pipe(
                take(1),
                map(() => {
                  return new UpdateWipPackerStore(updatedWipPackerState);
                }),
              );
            }
          }),
        ),
      ),
    );
  });

  LoadBctxData$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadBctxData>(TWipPackerStoreActionsType.LoadBctxData),
      switchMap((action) =>
        this.wipPackerStoreFacade.getBctxPayload().pipe(
          take(1),
          map((payload) => [action, payload] as const),
        ),
      ),
      switchMap(([action, payload]) =>
        from(
          this.inputAppAPIHelperUtil.presentLoading(
            this.translateService.instant('packing_db_effect.fetching_bctx_data'),
          ),
        ).pipe(
          take(1),
          map(() => [action, payload] as const),
        ),
      ),
      switchMap(([action, payload]) =>
        this.inputAppDataService.getBusninessContextData(payload).pipe(
          map((res) => {
            this.inputAppAPIHelperUtil.dismissLoading();
            return new LoadBctxDataSuccess(res);
          }),
          catchError((error) =>
            from(
              this.inputAppAPIHelperUtil.showPopup(
                this.translateService.instant('common.error'),
                this.translateService.instant('packing_common.something_went_wrong'),
              ),
            ).pipe(
              take(1),
              map(() => {
                console.log('[PACKING_CONFIG_ERROR]', error);
                this.inputAppAPIHelperUtil.dismissLoading();
                return new NoWipPackerAction();
              }),
            ),
          ),
        ),
      ),
    ),
  );

  LoadBctxDataSuccess$ = createEffect(() =>
    this.action$.pipe(
      ofType<LoadBctxDataSuccess>(TWipPackerStoreActionsType.LoadBctxDataSuccess),
      switchMap((action) => {
        return this.appMasterDBFacade.getSelectedStation().pipe(
          take(1),
          map((station) => [action, station] as const),
        );
      }),
      switchMap(([action, station]) =>
        this.masterdataListDBFacade.getBctxItemsOrderWithoutSize().pipe(
          take(1),
          map(
            (bctxKeysOrder) =>
              new FillLineMap(
                this.wipPackerStoreFacade.getLineMapFromBctxData(action.res, bctxKeysOrder, station?.subject || ''),
              ),
          ),
        ),
      ),
    ),
  );

  FillLineMap$ = createEffect(() => {
    return this.action$.pipe(
      ofType<FillLineMap>(TWipPackerStoreActionsType.FillLineMap),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.lineMap = action.lineMap;
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  SaveCarton$ = createEffect(() =>
    this.action$.pipe(
      ofType<SaveCarton>(TWipPackerStoreActionsType.SaveCarton),
      switchMap((action) =>
        from(
          this.appNotificationService.presentLoading(this.translateService.instant('wip_packer.effects.savingCarton')),
        ).pipe(
          take(1),
          map((loader) => [action, loader] as const),
        ),
      ),
      switchMap(([action, loader]) =>
        this.wipPackerStoreFacade.getWipPackerSavePayload(action.carton).pipe(
          take(1),
          map((payload) => {
            return [action, payload, loader] as const;
          }),
        ),
      ),
      switchMap(([action, payload, loader]) =>
        this.wipPackerDataService.save(payload).pipe(
          map((res) => {
            this.appNotificationService.dismissLoadingV2(loader);
            if (res.records.length > 0 && res.records[0].valid) {
              return new SaveCartonSuccess(action.carton, action.index);
            } else if (res.records.length > 0 && !res.records[0].valid && res.records[0].failure_info) {
              this.appNotificationService.showPopup(
                this.translateService.instant('wip_packer.effects.error'),
                res.records[0].failure_info.reason,
              );
              console.log('[SAVE_CARTON_FAILURE]', res);
              return new SaveCartonFailure(res.records[0].failure_info.reason, action.carton);
            } else {
              this.appNotificationService.showPopup(
                this.translateService.instant('wip_packer.effects.error'),
                this.translateService.instant('wip_packer.effects.somethingWentWrong'),
              );
              return new NoWipPackerAction();
            }
          }),
          catchError((error) => {
            this.appNotificationService.dismissLoadingV2(loader);
            this.appNotificationService.showPopup(
              this.translateService.instant('wip_packer.effects.error'),
              this.translateService.instant('wip_packer.effects.somethingWentWrong'),
            );
            console.log('[SAVE_CARTON_ERROR]', error);
            return [new NoWipPackerAction()];
          }),
        ),
      ),
    ),
  );

  UndoCarton$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UndoCarton>(TWipPackerStoreActionsType.UndoCarton),
      switchMap((action) =>
        this.wipPackerStoreFacade.getUndoList().pipe(
          take(1),
          map((undoList) => {
            if (undoList.length > 0) {
              const undoCarton = undoList[undoList.length - 1];
              const updatedCarton: TWipPackerCarton = {
                ...undoCarton.carton,
                type: 'UNDO',
                datetime: new Date().getTime(),
                cartonTotalQty: undoCarton.carton.cartonTotalQty - undoCarton.carton.currentCartonCount,
              };
              return new SaveCarton(updatedCarton, undoCarton.index);
            } else {
              this.appNotificationService.showPopup(
                this.translateService.instant('wip_packer.effects.noCartonToUndo'),
                'warning',
              );
              return new NoWipPackerAction();
            }
          }),
        ),
      ),
    );
  });

  SaveCartonSuccess$ = createEffect(() => {
    return this.action$.pipe(
      ofType<SaveCartonSuccess>(TWipPackerStoreActionsType.SaveCartonSuccess),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            return this.wipPackerStoreFacade.getPageConfigByViewName('pkg_landing_page').pipe(
              take(1),
              map((pageConfig) => [res, pageConfig as TWipPackerLandingViewDescriptor] as const),
            );
          }),
          switchMap(([res, pageConfig]) => {
            let undoList = res.undoList;
            if (action.carton.type === 'ADD') {
              const undoLimit = pageConfig.undo_times || 1;

              undoList = res.undoList.length >= undoLimit ? res.undoList.slice(1) : res.undoList;
              undoList.push({ carton: action.carton, index: action.index });
            } else {
              undoList = res.undoList.slice(0, -1); // remove the last item
            }
            const updatedState = produce(res, (draft) => {
              draft.undoList = undoList;
              draft.cartonList = [action.carton, ...draft.cartonList];
              draft.currentCartons = draft.currentCartons.map((item, index) =>
                index !== action.index ? item : action.carton,
              );
              const direction = action.carton.type === 'ADD' ? 1 : -1;
              if (draft.counts[action.carton.groupKey]) {
                draft.counts[action.carton.groupKey] = {
                  packedPacks:
                    draft.counts[action.carton.groupKey].packedPacks + direction * action.carton.currentCartonCount,
                  packedPieces:
                    draft.counts[action.carton.groupKey].packedPieces +
                    action.carton.packTotal * action.carton.currentCartonCount * direction,
                  lastUpdatedTimestamp: new Date().getTime(),
                };
              } else {
                draft.counts[action.carton.groupKey] = {
                  packedPacks: action.carton.currentCartonCount,
                  packedPieces: action.carton.packTotal * action.carton.currentCartonCount,
                  lastUpdatedTimestamp: new Date().getTime(),
                };
              }
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  SaveCartonFailure$ = createEffect(() => {
    return this.action$.pipe(
      ofType<SaveCartonFailure>(TWipPackerStoreActionsType.SaveCartonFailure),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.failedEvents = [...draft.failedEvents, { carton: action.carton, error: action.error }];
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  DeleteCarton$ = createEffect(() => {
    return this.action$.pipe(
      ofType<DeleteCarton>(TWipPackerStoreActionsType.DeleteCarton),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.currentCartons = draft.currentCartons.filter((item, index) => index !== action.index);
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  setsetCurrentCarton = createEffect(() => {
    return this.action$.pipe(
      ofType<SetCurrentCarton>(TWipPackerStoreActionsType.setCurrentCarton),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.currentCartons = [...res.currentCartons, action.carton];
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  setSelectedBctxAndHeader = createEffect(() => {
    return this.action$.pipe(
      ofType<SetSelectedBctxAndHeader>(TWipPackerStoreActionsType.setSelectedBctxAndHeader),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.selectedBctx = action.selectedBctx;
              draft.selectedBctxHeader = action.selectedBctxHeader;
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });

  CompleteCarton$ = createEffect(() => {
    return this.action$.pipe(
      ofType<CompleteCarton>(TWipPackerStoreActionsType.CompleteCarton),
      switchMap((action) =>
        from(this.persistentStorageService.get('[APP][INPUT_APP_WIP_PACKER_DB]')).pipe(
          take(1),
          switchMap((res) => {
            const updatedState = produce(res, (draft) => {
              draft.currentCartons = draft.currentCartons.map((item, index) =>
                index === action.index ? action.carton : item,
              );
            });
            return from(this.persistentStorageService.set('[APP][INPUT_APP_WIP_PACKER_DB]', updatedState)).pipe(
              take(1),
              map(() => {
                return new UpdateWipPackerStore(updatedState);
              }),
            );
          }),
        ),
      ),
    );
  });
}
